'use client'

import React from 'react'
import { createPortal, flushSync } from 'react-dom'
import Link from 'next/link'
import { LogOut, PenLine, Search, Settings, User, X } from 'lucide-react'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import event from '@/utils/event'
import Wallet from '@/utils/wallet'
import ButtonGradientEpic from '@/components/button-gradient-epic'
import UserIconStatic from '@/components/user-icon-static'
import { CDN_URL, STORAGE_PROFILE } from '@/utils/constants'
import useLoggedIn, { useLogout } from '@/utils/hooks/useLoggedIn'
import { ambiguousNames, BTC_ID, isVerified } from '../lib/amm'
import { formatUsdPrice, usdCompact } from '../lib/format'
import { useAlkanes } from '../lib/store'
import { openProfileEditor } from '../lib/twetch-ui'
import useIsDesktop from '../lib/useIsDesktop'
import useTwetchIdentities from '../lib/useTwetchIdentities'
import type { AssetRow as Asset } from '../lib/useAssets'
import AssetBadges, { nameFlags } from './asset-badges'
import { BadgedIcon, Change, McChip } from './asset-row'
import { Avatar } from './social-feed'
import NotificationsBell from './notifications-bell'

/// Token discovery stays in the app chrome: desktop gets the full field with
/// a dropdown, phones get a field-shaped door that opens a full-screen sheet
/// over the same registry-backed results. `/` focuses the right control for
/// the current viewport.
function TokenSearch() {
	const assets = useAlkanes((s) => s.assets)
	const verifiedIds = useAlkanes((s) => s.verifiedIds)
	const navigate = useAlkanes((s) => s.navigate)
	const [open, setOpen] = React.useState(false)
	const [query, setQuery] = React.useState('')
	const inputRef = React.useRef<HTMLInputElement>(null)
	const mobileInputRef = React.useRef<HTMLInputElement>(null)
	const boxRef = React.useRef<HTMLDivElement>(null)
	const desktop = useIsDesktop()

	// iOS only raises the keyboard for a focus() made inside the tap itself,
	// so the sheet is committed synchronously and focused in the same task.
	const openSheet = () => {
		flushSync(() => setOpen(true))
		mobileInputRef.current?.focus()
	}
	const close = () => {
		setOpen(false)
		setQuery('')
	}

	React.useEffect(() => {
		const onKey = (e: KeyboardEvent) => {
			if (
				e.key === '/' &&
				!(e.target instanceof HTMLInputElement) &&
				!(e.target instanceof HTMLTextAreaElement)
			) {
				e.preventDefault()
				if (window.matchMedia('(min-width: 1024px)').matches) {
					inputRef.current?.focus()
				} else {
					openSheet()
				}
			}
		}
		window.addEventListener('keydown', onKey)
		return () => window.removeEventListener('keydown', onKey)
	}, [])

	// Click-away closes the dropdown.
	React.useEffect(() => {
		if (!open || !desktop) {
			return
		}
		const onDown = (e: MouseEvent) => {
			if (boxRef.current && !boxRef.current.contains(e.target as Node)) {
				setOpen(false)
			}
		}
		window.addEventListener('mousedown', onDown)
		return () => window.removeEventListener('mousedown', onDown)
	}, [open, desktop])

	// The sheet owns the screen: the page behind it must not scroll.
	React.useEffect(() => {
		if (!open || desktop) {
			return
		}
		const previous = document.body.style.overflow
		document.body.style.overflow = 'hidden'
		return () => {
			document.body.style.overflow = previous
		}
	}, [open, desktop])

	// The shared-name set, so the dropdown carries the SAME identity marks the
	// mobile rows do — five live tokens answer to "pi", and a surface that
	// lists them without the ⚠ is a surface that helps an impostor.
	const ambiguous = React.useMemo(
		() => ambiguousNames(assets.map((a) => ({ id: a.id, name: a.name }))),
		[assets]
	)
	const additionalVerified = React.useMemo(
		() => assets.filter((candidate) => verifiedIds.includes(candidate.id)),
		[assets, verifiedIds]
	)

	const q = query.trim().toLowerCase()
	const rows = React.useMemo(() => {
		if (!q) {
			// Trending: the volume ranking, same as the token list's default.
			return [...assets]
				.sort((a, b) => (b.volume_24h_usd ?? -1) - (a.volume_24h_usd ?? -1))
				.slice(0, desktop ? 6 : 10)
		}
		return assets
			.filter((a) => (a.name || '').toLowerCase().includes(q) || a.id.toLowerCase().includes(q))
			.sort((a, b) => {
				const score = (x: Asset) =>
					(isVerified(x.id, verifiedIds) ? 2 : 0) +
					((x.name || '').toLowerCase().startsWith(q) ? 1 : 0)
				const d = score(b) - score(a)
				return d !== 0 ? d : b.liquidity_usd - a.liquidity_usd
			})
			.slice(0, desktop ? 8 : 25)
	}, [assets, verifiedIds, q, desktop])

	const pick = (a: Asset) => {
		navigate('home', a.id)
		setOpen(false)
		setQuery('')
		inputRef.current?.blur()
		mobileInputRef.current?.blur()
	}

	const onInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === 'Escape') {
			close()
			e.currentTarget.blur()
		}
		if (e.key === 'Enter' && rows[0]) {
			pick(rows[0])
		}
	}

	const results = (
		<>
		<p className="px-2.5 pb-1 pt-1.5 text-xs font-semibold text-muted-foreground">
			{q ? (rows.length === 0 ? 'No results' : 'Results') : 'Trending now'}
		</p>
		{rows.length === 0 ? (
			<div className="px-2.5 py-8 text-center" data-testid="alkanes-search-empty">
				<p className="text-sm font-semibold">No token matches “{query.trim()}”</p>
				<p className="mx-auto mt-1 max-w-[34ch] text-balance text-xs leading-relaxed text-muted-foreground">
					Search by name, or paste an alkane id like <span className="tabular-nums">2:0</span>.
				</p>
			</div>
		) : (
			// The mobile feed row's anatomy, dropdown-sized: badged icon,
			// name + warning marks, then the impostor callout or the
			// MC chip — with liquidity spelled out on shared-name rows,
			// because their warning tells the user to CHECK liquidity.
			rows.map((a) => {
				const flags = nameFlags(a, ambiguous, additionalVerified)
				return (
					<button
						key={a.id}
						type="button"
						data-testid={`alkanes-search-hit-${a.id}`}
						onClick={() => pick(a)}
						className="flex min-h-14 w-full items-center gap-2.5 rounded-xl px-2.5 py-2 text-left transition-colors hover:bg-secondary/60 active:bg-secondary lg:min-h-0"
					>
						<BadgedIcon
							token={{ id: a.id, name: a.name, icon: a.icon }}
							verified={flags.verified}
							size={28}
						/>
						<span className="min-w-0 flex-1">
							<span className="flex items-center gap-1.5">
								<span className="truncate text-sm font-bold leading-tight">
									{a.name || a.id}
								</span>
								{!flags.verified && <AssetBadges flags={flags} name={a.name} />}
							</span>
							{flags.impostorOf ? (
								<span className="block truncate text-xxs tabular-nums text-destructive-ink">
									Not {a.name} ({flags.impostorOf}) — unverified {a.id}
								</span>
							) : (
								<span className="mt-0.5 flex items-center gap-1.5">
									<McChip mc={a.market_cap_usd} />
									<span className="truncate text-xxs tabular-nums text-muted-foreground">
										{a.id}
										{flags.ambiguous
											? ` · ${usdCompact(a.liquidity_usd)} liquidity`
											: a.volume_24h_usd !== null
											? ` · ${usdCompact(a.volume_24h_usd)} vol`
											: ''}
									</span>
								</span>
							)}
						</span>
						<span className="flex shrink-0 flex-col items-end gap-0.5">
							<span className="text-xs font-semibold tabular-nums">
								{a.price_usd !== null ? formatUsdPrice(a.price_usd) : '—'}
							</span>
							<Change v={a.change_24h_pct} className="text-xxs" />
						</span>
					</button>
				)
			})
		)}
		</>
	)

	return (
		<div ref={boxRef} className="relative min-w-0 flex-1 lg:w-full lg:max-w-[520px] lg:flex-none">
			{/* Phones: a door shaped like the field it opens, so search reads as
			    search — not as one more anonymous icon in the bar. */}
			<button
				type="button"
				aria-label="Search tokens"
				aria-haspopup="dialog"
				aria-expanded={open}
				data-testid="alkanes-mobile-search"
				onClick={openSheet}
				className="flex h-10 w-full items-center gap-2 rounded-xl bg-secondary/50 px-3 text-left text-sm text-muted-foreground transition-colors active:bg-secondary lg:hidden"
			>
				<Search className="size-4 shrink-0" aria-hidden="true" />
				<span className="truncate">Search</span>
			</button>
			<div className="hidden items-center gap-2 rounded-xl bg-secondary/50 px-3 lg:flex">
				<Search className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
				<input
					ref={inputRef}
					type="search"
					role="combobox"
					aria-expanded={open}
					aria-controls="alkanes-search-results"
					value={query}
					onFocus={() => setOpen(true)}
					onChange={(e) => {
						setQuery(e.target.value)
						setOpen(true)
					}}
					onKeyDown={onInputKeyDown}
					aria-label="Search tokens"
					placeholder="Search name or id"
					data-testid="alkanes-top-search"
					className="h-10 w-full min-w-0 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
				/>
				<kbd
					aria-hidden="true"
					className="rounded border border-border px-1.5 text-xxs text-muted-foreground"
				>
					/
				</kbd>
			</div>
			{open && desktop && (
				<div
					id="alkanes-search-results"
					data-testid="alkanes-search-results"
					className="absolute inset-x-0 top-12 z-50 overflow-hidden rounded-2xl border border-border/60 bg-popover p-1.5 shadow-xl"
				>
					{results}
				</div>
			)}
			{/* Portaled: the header's backdrop-filter would otherwise become the
			    containing block and clip a fixed sheet to the 56px bar. */}
			{open &&
				!desktop &&
				createPortal(
					<div
						role="dialog"
						aria-modal="true"
						aria-label="Search tokens"
						className="alkanes-ink alkanes-sheet-in fixed inset-0 z-50 flex flex-col bg-background"
					>
						<div className="flex h-14 shrink-0 items-center gap-1 border-b border-border/60 pl-4 pr-1">
							<div className="flex min-w-0 flex-1 items-center gap-2 rounded-xl bg-secondary/50 px-3">
								<Search className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
								<input
									ref={mobileInputRef}
									type="search"
									role="combobox"
									aria-expanded="true"
									aria-controls="alkanes-search-results"
									aria-label="Search tokens"
									value={query}
									onChange={(e) => setQuery(e.target.value)}
									onKeyDown={onInputKeyDown}
									placeholder="Search name or id"
									enterKeyHint="search"
									autoCapitalize="none"
									autoCorrect="off"
									spellCheck={false}
									data-testid="alkanes-mobile-search-input"
									// 16px: anything smaller makes iOS zoom the page on focus.
									className="h-10 w-full min-w-0 bg-transparent text-base outline-none placeholder:text-muted-foreground [&::-webkit-search-cancel-button]:hidden"
								/>
								{query && (
									<button
										type="button"
										aria-label="Clear search"
										onClick={() => {
											setQuery('')
											mobileInputRef.current?.focus()
										}}
										className="-mr-2 grid size-10 shrink-0 place-items-center text-muted-foreground"
									>
										<span className="grid size-5 place-items-center rounded-full bg-muted">
											<X className="size-3" aria-hidden="true" />
										</span>
									</button>
								)}
							</div>
							<button
								type="button"
								data-testid="alkanes-mobile-search-cancel"
								onClick={close}
								className="alkanes-touch h-11 shrink-0 px-3 text-sm font-semibold text-primary"
							>
								Cancel
							</button>
						</div>
						<div
							id="alkanes-search-results"
							data-testid="alkanes-search-results"
							className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-1.5 pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-1"
						>
							{results}
						</div>
					</div>,
					document.body
				)}
		</div>
	)
}

/// Desktop wallet cluster: the spendable-BTC "cash" card and the portfolio
/// card, plus the SITE'S OWN wallet entry point — the real HeaderProfile
/// account menu when connected and the real gradient Connect Wallet button
/// when not. Those two carry the provider integrations and connected-state
/// behavior; this bar only re-homes them. No 24h delta on the portfolio — no
/// historical balance series exists, and a delta reconstructed from today's
/// prices would be an invention.
/// The signed-in avatar: the linked Twetch identity's pfp when there is one,
/// else the wallet's own OW profile icon, else the pubkey-seeded gradient.
function AccountAvatar({ size }: { size: number }) {
	const address = Wallet.getTaprootAddress()
	const identities = useTwetchIdentities([address])
	const twetchUser = address ? identities[address] : undefined
	// Lazy initializer, not an effect: this subtree never renders on the
	// server (see terminal.tsx), so localStorage is readable on first render.
	const [owIcon] = React.useState<string | null>(() => {
		try {
			const parsed = JSON.parse(localStorage.getItem(STORAGE_PROFILE) ?? '')
			return parsed?.icon ? String(parsed.icon) : null
		} catch {
			// no stored OW profile — the gradient fallback carries it
			return null
		}
	})
	if (twetchUser?.icon) {
		return <Avatar user={twetchUser} userId={twetchUser.id} size={size} />
	}
	const gradientId = parseInt(Wallet.getPublicKey() || '0', 16)
	return (
		<UserIconStatic
			src={owIcon ? `${CDN_URL}/${owIcon}` : undefined}
			defaultGradientId={gradientId}
			size={size}
		/>
	)
}

/// Desktop wallet cluster: the spendable-BTC "cash" card, and the fomo-style
/// portfolio card — total, "Portfolio", the account avatar — that opens the
/// account menu (profile / settings / sign out). The real gradient Connect
/// Wallet button when not connected. No 24h delta on the portfolio — no
/// historical balance series exists, and a delta reconstructed from today's
/// prices would be an invention.
function WalletCluster() {
	const loggedIn = useLoggedIn()
	const balances = useAlkanes((s) => s.balances)
	const balancesUsd = useAlkanes((s) => s.balancesUsd)
	const btcUsd = useAlkanes((s) => s.btcUsd)
	const openProfile = useAlkanes((s) => s.openProfile)
	const logout = useLogout()
	const [menuOpen, setMenuOpen] = React.useState(false)
	// Hover opens the menu and a plain click goes straight to the profile —
	// but only where hover exists. On coarse pointers the click keeps opening
	// the menu, or settings and sign-out would be unreachable on a phone.
	const [hoverable] = React.useState(
		() => typeof window !== 'undefined' && window.matchMedia('(hover: hover)').matches
	)
	const closeTimer = React.useRef<number | null>(null)
	const openMenu = () => {
		if (closeTimer.current !== null) {
			window.clearTimeout(closeTimer.current)
			closeTimer.current = null
		}
		setMenuOpen(true)
	}
	// A small grace period so the pointer can cross the gap between the
	// trigger and the portaled panel without the menu snapping shut.
	const scheduleClose = () => {
		if (closeTimer.current !== null) {
			window.clearTimeout(closeTimer.current)
		}
		closeTimer.current = window.setTimeout(() => setMenuOpen(false), 150)
	}

	const total = React.useMemo(() => {
		let sum = 0
		let priced = 0
		for (const v of balancesUsd.values()) {
			sum += v
			priced += 1
		}
		return priced > 0 ? sum : null
	}, [balancesUsd])
	const btcRaw = balances.get(BTC_ID) ?? 0n
	const cashUsd = btcUsd !== null ? (Number(btcRaw) / 1e8) * btcUsd : null

	if (!loggedIn) {
		return (
			<ButtonGradientEpic
				size="small"
				data-testid="alkanes-top-connect"
				className="inline-flex h-10 items-center justify-center whitespace-nowrap px-3 sm:px-5"
				onClick={(evt: React.MouseEvent) => {
					evt.stopPropagation()
					evt.preventDefault()
					event.emit('command', { id: 'connect-wallet' })
				}}
			>
				Connect Wallet
			</ButtonGradientEpic>
		)
	}

	const menuItem =
		'flex w-full items-center gap-2.5 rounded-lg px-3 py-2.5 text-left text-sm font-semibold transition-colors hover:bg-secondary/60'

	return (
		<div className="flex items-center gap-2">
			<Link
				href="/wallet"
				data-testid="alkanes-top-cash"
				className="hidden flex-col items-end rounded-xl bg-secondary/50 px-3 py-1.5 transition-colors hover:bg-secondary lg:flex"
			>
				<span className="text-sm font-bold tabular-nums leading-tight">
					{cashUsd !== null ? `${usdCompact(cashUsd)} BTC` : '— BTC'}
				</span>
				<span className="text-xxs font-semibold text-primary">Deposit more</span>
			</Link>
			<NotificationsBell ink />
			<Popover
				open={menuOpen}
				onOpenChange={(open) => {
					// Radix's own toggle only drives the menu on coarse pointers;
					// with hover, the trigger's click means "go to my profile" and
					// only outside-click/Escape may close through here.
					if (!open) {
						setMenuOpen(false)
					} else if (!hoverable) {
						setMenuOpen(true)
					}
				}}
			>
				<PopoverTrigger asChild>
					<button
						type="button"
						data-testid="alkanes-top-portfolio"
						aria-label="Account menu"
						aria-haspopup="menu"
						aria-expanded={menuOpen}
						onMouseEnter={hoverable ? openMenu : undefined}
						onMouseLeave={hoverable ? scheduleClose : undefined}
						onClick={
							hoverable
								? () => {
										setMenuOpen(false)
										const addr = Wallet.getTaprootAddress()
										if (addr) {
											openProfile(addr)
										}
								  }
								: undefined
						}
						className="flex items-center gap-2.5 rounded-xl px-1.5 py-1 transition-colors hover:bg-secondary lg:bg-secondary/50 lg:px-3 lg:py-1.5"
					>
						<span className="hidden flex-col items-end lg:flex">
							<span className="text-sm font-bold tabular-nums leading-tight">
								{total !== null
									? `$${total.toLocaleString('en-US', {
											minimumFractionDigits: 2,
											maximumFractionDigits: 2
									  })}`
									: '—'}
							</span>
							<span className="text-xxs text-muted-foreground">Portfolio</span>
						</span>
						<AccountAvatar size={32} />
					</button>
				</PopoverTrigger>
				<PopoverContent
					align="end"
					className="alkanes-ink w-56 p-1.5"
					role="menu"
					onMouseEnter={hoverable ? openMenu : undefined}
					onMouseLeave={hoverable ? scheduleClose : undefined}
					// A hover-open must not yank keyboard focus into the panel.
					onOpenAutoFocus={hoverable ? (e) => e.preventDefault() : undefined}
				>
					<button
						type="button"
						role="menuitem"
						data-testid="alkanes-account-profile"
						className={menuItem}
						onClick={() => {
							setMenuOpen(false)
							const addr = Wallet.getTaprootAddress()
							if (addr) {
								openProfile(addr)
							}
						}}
					>
						<User className="size-4 text-muted-foreground" aria-hidden="true" />
						Your profile
					</button>
					<button
						type="button"
						role="menuitem"
						data-testid="alkanes-account-edit-profile"
						className={menuItem}
						onClick={() => {
							setMenuOpen(false)
							openProfileEditor()
						}}
					>
						<PenLine className="size-4 text-muted-foreground" aria-hidden="true" />
						Edit profile
					</button>
					<Link
						href="/settings"
						role="menuitem"
						data-testid="alkanes-account-settings"
						className={menuItem}
						onClick={() => setMenuOpen(false)}
					>
						<Settings className="size-4 text-muted-foreground" aria-hidden="true" />
						Settings
					</Link>
					<button
						type="button"
						role="menuitem"
						data-testid="alkanes-account-signout"
						className={`${menuItem} text-destructive-ink`}
						onClick={() => {
							setMenuOpen(false)
							// The site's own sign-out flow: OW-provider wallets get the
							// confirm modal (seed custody), external providers detach.
							if (Wallet.type() === 'ordinalswallet') {
								event.emit('open-signout-modal')
								return
							}
							logout()
						}}
					>
						<LogOut className="size-4" aria-hidden="true" />
						Sign out
					</button>
				</PopoverContent>
			</Popover>
		</div>
	)
}

/// The app's own chrome: brand mark left, token search at every width, and the
/// wallet cluster. The empty notification door yields to search on phones.
export default function TopBar() {
	return (
		<React.Fragment>
		<header
			data-testid="alkanes-top-bar"
			className="fixed inset-x-0 top-0 z-40 border-b border-border/60 bg-background/90 backdrop-blur"
		>
			<div className="mx-auto flex h-14 w-full items-center justify-between gap-3 px-4 lg:gap-6 lg:px-5">
				{/* The site's own brand lockup, linked home — the way out of the
				    full-screen app is the same mark the rest of the site wears. */}
				<Link
					href="/"
					aria-label="Ordinals Wallet home"
					className="flex shrink-0 items-center gap-2 select-none"
				>
					<img src="/logo-icon.svg" alt="" width={24} height={24} className="size-6" />
					<span className="hidden text-[15px] font-semibold tracking-tight sm:block">
						Ordinals Wallet
					</span>
					<span className="rounded bg-primary/15 px-1.5 py-0.5 text-xxs font-bold text-primary">
						Alkanes
					</span>
				</Link>
				<TokenSearch />
				<div className="flex shrink-0 items-center gap-2">
					<WalletCluster />
				</div>
			</div>
		</header>
		{/* The header is fixed; this keeps its 3.5rem in the flow. */}
		<div aria-hidden="true" className="h-14" />
		</React.Fragment>
	)
}
